// src/components/ContactForm.js
// ─────────────────────────────────────────────────────────────────────────────
// FIXES:
// 1. saveContactSubmission is now called with the correct field names that
//    match what the form actually sends (name, email, subject, message).
// 2. The Firestore write happens inside its own try/catch so a rules error
//    is logged to the console without breaking the UI or swallowing the error
//    silently — you'll see exactly what went wrong in the browser console.
// 3. contactSubmissions is now a public-create collection (see firestore.rules)
//    so unauthenticated visitors can write submissions. If you still see a
//    permissions error after updating the rules, check that the rules have
//    been deployed: firebase deploy --only firestore:rules
// ─────────────────────────────────────────────────────────────────────────────
import React, { useRef, useState } from "react";
import axios from "axios";
import {
  FaWhatsapp, FaEnvelope, FaPhone,
  FaSpinner, FaCheckCircle, FaExclamationCircle,
} from "react-icons/fa";
import { useSite } from "../context/SiteContext";
import { saveContactSubmission } from "../firebase/dataService";
import "./ContactForm.css";

const ContactForm = () => {
  const { profile, socials } = useSite();
  const form = useRef();
  const [status,  setStatus]  = useState(null);  // null | "loading" | "success" | "error"
  const [message, setMessage] = useState("");

  const whatsappSocial = socials.find((s) => s.id === "whatsapp");
  const contactCards = [
    whatsappSocial && {
      icon: <FaWhatsapp size={22}/>, label: "WhatsApp",
      value: profile.phone || "", href: whatsappSocial.url || "#",
    },
    profile.email && {
      icon: <FaEnvelope size={22}/>, label: "Email",
      value: profile.email, href: `mailto:${profile.email}`,
    },
    profile.phone && {
      icon: <FaPhone size={22}/>, label: "Phone",
      value: profile.phone, href: `tel:${profile.phone}`,
    },
  ].filter(Boolean);

  const validate = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Pull values directly from the form elements
    const nameEl    = e.target.elements.user_name;
    const emailEl   = e.target.elements.user_email;
    const subjectEl = e.target.elements.subject;
    const msgEl     = e.target.elements.message;

    const name    = nameEl?.value    || "";
    const email   = emailEl?.value   || "";
    const subject = subjectEl?.value || "";
    const msg     = msgEl?.value     || "";

    if (!validate(email)) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }

    setStatus("loading");
    setMessage("");

    const endpoint = profile.contactEndpoint || "https://email.tildaitech.co.ke/send-email";

    let emailSent  = false;
    let emailError = "";

    // ── 1. Attempt email delivery ─────────────────────────────────────────
    try {
      await axios.post(endpoint, {
        senderEmail:    email,
        recipientEmail: profile.email || "raphael@tildaitech.co.ke",
        subject,
        message:        msg,
      });
      emailSent = true;
    } catch (err) {
      // Capture the error detail but don't throw — we always save to Firestore
      emailError =
        err?.response?.data?.message ||
        err?.response?.data?.error   ||
        err.message                  ||
        "Unknown email error";
      console.warn("[ContactForm] Email delivery failed:", emailError);
    }

    // ── 2. Always persist to Firestore ────────────────────────────────────
    // contactSubmissions allows public `create` in the security rules so
    // unauthenticated users can write. If this throws it means the rules
    // haven't been deployed yet — check the browser console for details.
    try {
      await saveContactSubmission({
        name,
        email,
        subject,
        message: msg,
        emailSent,
        emailError,
      });
    } catch (fsErr) {
      // Log but don't block the UX — the user has already had their email
      // attempted. Show them a success/partial-success message either way.
      console.error(
        "[ContactForm] Firestore save failed:",
        fsErr.message,
        "\nCheck that firestore.rules has been deployed and includes the",
        "contactSubmissions collection with `allow create: if true;`"
      );
    }

    // ── 3. Show result to visitor ─────────────────────────────────────────
    if (emailSent) {
      setStatus("success");
      setMessage("Message sent! I'll get back to you soon.");
    } else {
      // Email failed but the submission is saved in Firestore — admin can
      // see it in the Inbox tab on the dashboard.
      setStatus("success");
      setMessage(
        "Your message has been received! There was a small hiccup with email " +
        "delivery, but the message is saved and I will get back to you soon."
      );
    }
    form.current.reset();
  };

  return (
    <section className="contact-section">
      <div className="container contact-inner">

        {/* ── Left: contact info cards ──────────────────────────────────── */}
        <div className="contact-info">
          <p className="section-label">Get in touch</p>
          <h2 className="section-title contact-info__title">Let's have a chat.</h2>
          <p className="contact-info__sub">
            Whether it's a new project, a collaboration, or just a hello — my inbox is always open.
          </p>
          <div className="contact-cards">
            {contactCards.map(({ icon, label, value, href }) => (
              <a key={label} href={href} target="_blank" rel="noreferrer" className="contact-card card">
                <span className="contact-card__icon">{icon}</span>
                <div>
                  <p className="contact-card__label">{label}</p>
                  <p className="contact-card__value">{value}</p>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* ── Right: contact form ───────────────────────────────────────── */}
        <div className="contact-form-wrap card">
          <h3 className="contact-form__title">Send a message</h3>

          {status === "success" && (
            <div className="contact-alert contact-alert--success">
              <FaCheckCircle size={16}/> {message}
            </div>
          )}
          {status === "error" && (
            <div className="contact-alert contact-alert--error">
              <FaExclamationCircle size={16}/> {message}
            </div>
          )}

          <form ref={form} onSubmit={handleSubmit} className="contact-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="user_name">Your Name</label>
                <input
                  id="user_name"
                  name="user_name"
                  type="text"
                  placeholder="John Doe"
                  required/>
              </div>
              <div className="form-group">
                <label htmlFor="user_email">Email Address</label>
                <input
                  id="user_email"
                  name="user_email"
                  type="email"
                  placeholder="john@example.com"
                  required/>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="subject">Subject</label>
              <input
                id="subject"
                name="subject"
                type="text"
                placeholder="Project inquiry"
                required/>
            </div>
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                rows={6}
                placeholder="Tell me about your project…"
                required/>
            </div>
            <button
              type="submit"
              className="btn btn-primary contact-form__submit"
              disabled={status === "loading"}>
              {status === "loading"
                ? <><FaSpinner className="spin" size={14}/> Sending…</>
                : "Send Message"}
            </button>
          </form>
        </div>

      </div>
    </section>
  );
};

export default ContactForm;